/**
 * Orchestrates pushing/pulling progress to Supabase.
 *
 * Fully inert when accounts aren't configured or no one is signed in — no
 * listeners registered, zero behavioural change to anonymous mode. This file
 * is the only place that combines `syncCore.js`'s pure logic with actual
 * network calls and the app's event bus; `progressRepository.js` itself never
 * imports anything from here (see that file's own header comment on why).
 */

import { getSupabase, isSupabaseConfigured } from '../lib/supabaseClient.js';
import { getCurrentUser } from './authService.js';
import { decideMergeStrategy, fromRemoteRow, hasRealProgress, toRemoteRow } from './syncCore.js';
import { debounce } from '../utils/debounce.js';
import { emit, on, EVENTS } from '../app/eventBus.js';
import { getUserProgress, mergeProgress } from '../repositories/progressRepository.js';
import { formatNumber, relativeTime } from '../utils/format.js';
import { readJSON, writeJSON } from '../utils/storage.js';
import { STORAGE_KEYS } from '../app/config.js';
import { el } from '../utils/dom.js';
import { openModal } from '../components/Modal.js';

const PUSH_DEBOUNCE_MS = 4000;
const PUSH_MAX_WAIT_MS = 15000;
const TABLE = 'user_progress';

let status = 'idle'; // idle | syncing | synced | offline | error | disabled
let lastSyncedAt = null;
let pendingPush = false;
let started = false;
// The account this page has already reconciled with (or is reconciling with now).
// Supabase announces the same session again on every tab focus and token refresh;
// those repeats must not start the sign-in reconciliation again.
let reconciledUserId = null;

/**
 * This device's record of when it last matched an account: the `updatedAt` each
 * side had at that moment. It is what lets later visits sync without a question
 * (see decideMergeStrategy).
 */
function readLink(userId) {
  const link = readJSON(STORAGE_KEYS.syncLink, null);
  return link && link.userId === userId ? link : null;
}

function saveLink(userId, localUpdatedAt, remoteUpdatedAt) {
  writeJSON(STORAGE_KEYS.syncLink, { userId, local: localUpdatedAt, remote: remoteUpdatedAt });
}

function setStatus(next) {
  status = next;
  emit(EVENTS.syncStatusChanged, { status, lastSyncedAt });
}

export function getSyncStatus() {
  return { status, lastSyncedAt };
}

async function pushProgress() {
  const user = getCurrentUser();
  if (!isSupabaseConfigured() || !user) return;

  setStatus('syncing');
  try {
    const row = toRemoteRow(getUserProgress(), user.id);
    const client = await getSupabase();
    const { error } = await client.from(TABLE).upsert(row, { onConflict: 'user_id' });
    if (error) throw error;
    // The account now holds exactly what this device had when the row was built.
    saveLink(user.id, row.updated_at, row.updated_at);
    pendingPush = false;
    lastSyncedAt = new Date().toISOString();
    setStatus('synced');
  } catch (error) {
    pendingPush = true;
    setStatus(navigator.onLine ? 'error' : 'offline');
    console.warn('[sync] push failed', error);
  }
}

const debouncedPush = debounce(pushProgress, PUSH_DEBOUNCE_MS, { maxWaitMs: PUSH_MAX_WAIT_MS });

async function pullProgress(userId) {
  const client = await getSupabase();
  const { data, error } = await client
    .from(TABLE)
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;
  return fromRemoteRow(data);
}

/** Applies a remote snapshot locally, preserving the device's own `questions` map. */
function applyRemote(remote, userId) {
  const saved = mergeProgress(remote);
  saveLink(userId, saved.updatedAt, remote.updatedAt);
}

function summarize(progress, label) {
  const xp = formatNumber(progress?.xp?.total || 0);
  const when = progress?.updatedAt ? relativeTime(progress.updatedAt) : 'never';
  return `${label}: ${xp} XP, last active ${when}`;
}

function askUserToChoose({ local, remote, onLocal, onRemote }) {
  openModal({
    title: 'Two sets of progress found',
    body: el('div.stack', {}, [
      el('p.text-muted', {
        text: 'This device and your account both have progress. Which one should be kept? The other will be replaced.'
      }),
      el('div.stack', { style: { gap: '8px' } }, [
        el('div.card', { style: { padding: '12px' }, text: summarize(local, 'This device') }),
        el('div.card', { style: { padding: '12px' }, text: summarize(remote, 'Your account') })
      ])
    ]),
    actions: [
      { label: 'Keep this device', variant: 'soft', onClick: onLocal },
      { label: 'Keep account progress', variant: 'primary', onClick: onRemote }
    ]
  });
}

/** Runs once per sign-in: reconcile local vs. remote before regular sync begins. */
async function handleSignIn(user) {
  if (reconciledUserId === user.id) return;
  reconciledUserId = user.id;

  setStatus('syncing');
  let remote;
  try {
    remote = await pullProgress(user.id);
  } catch (error) {
    console.warn('[sync] initial pull failed', error);
    setStatus('error');
    reconciledUserId = null; // let the next announcement of the session try again
    return;
  }

  const local = getUserProgress();
  const link = readLink(user.id);
  const decision = decideMergeStrategy({ local, remote, link });

  if (decision.action === 'in-sync') {
    saveLink(user.id, local.updatedAt, remote.updatedAt);
    lastSyncedAt = new Date().toISOString();
    setStatus('synced');
    return;
  }

  if (decision.action === 'use-remote') {
    applyRemote(remote, user.id);
    // Only worth announcing on a device that had nothing; a device already on the
    // account just catches up quietly.
    if (hasRealProgress(remote) && !link) {
      emit(EVENTS.toast, {
        title: 'Progress restored',
        body: 'Picked up where you left off on your account.',
        variant: 'success'
      });
    }
    setStatus('synced');
    lastSyncedAt = new Date().toISOString();
    return;
  }

  if (decision.action === 'use-local') {
    await pushProgress();
    return;
  }

  // Both sides have real, distinct progress — never guess.
  askUserToChoose({
    local,
    remote,
    onLocal: () => pushProgress(),
    onRemote: () => {
      applyRemote(remote, user.id);
      setStatus('synced');
      lastSyncedAt = new Date().toISOString();
    }
  });
}

function handleSignOut() {
  reconciledUserId = null;
  debouncedPush.cancel();
  pendingPush = false;
  lastSyncedAt = null;
  setStatus('disabled');
}

/** Called once from main.js. No-ops entirely when accounts aren't configured. */
export function initSync() {
  if (started || !isSupabaseConfigured()) return;
  started = true;
  setStatus('disabled');

  on(EVENTS.authChanged, ({ status: authStatus, user }) => {
    if (authStatus === 'signed-in' && user) handleSignIn(user);
    else handleSignOut();
  });

  on(EVENTS.progressChanged, () => {
    if (getCurrentUser()) debouncedPush.call();
  });

  const flush = () => debouncedPush.flush();
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) flush();
  });
  window.addEventListener('pagehide', flush);

  window.addEventListener('online', () => {
    if (pendingPush && getCurrentUser()) pushProgress();
  });
}

export function syncNow() {
  if (getCurrentUser()) return pushProgress();
  return Promise.resolve();
}
